<template>
  <section id="hero" ref="heroSection" class="relative w-full h-screen overflow-hidden">
    <!-- Debug controls - hidden by default -->
    <div v-if="showDebugControls" class="absolute right-4 top-4 z-[1000] bg-white/90 p-4 rounded-lg shadow-lg">
      <h3 class="text-lg font-bold mb-2">Debug Controls</h3>
      <div class="space-y-3">
        <div>
          <label class="block text-sm mb-1">Frame Progress: {{ currentFrame }} / {{ totalFrames }}</label>
          <input 
            type="range" 
            min="1" 
            max="240" 
            step="1" 
            :value="currentFrame" 
            @input="manualSetFrame($event.target.value)"
            class="w-full"
          >
        </div>
        <div class="flex space-x-2">
          <button 
            @click="manualSetLocked(false)" 
            :class="{'bg-green-500': !sequenceLocked, 'bg-gray-300': sequenceLocked}"
            class="px-2 py-1 text-white text-xs rounded"
          >
            Scrubbing
          </button>
          <button 
            @click="manualSetLocked(true)" 
            :class="{'bg-green-500': sequenceLocked, 'bg-gray-300': !sequenceLocked}"
            class="px-2 py-1 text-white text-xs rounded"
          >
            Locked
          </button>
        </div>
        <div class="flex space-x-2">
          <button 
            @click="releaseScroll" 
            class="px-2 py-1 bg-red-500 text-white text-xs rounded"
          >
            Release Scroll
          </button>
        </div>
      </div>
    </div>
    
    <!-- Error fallback -->
    <div 
      v-if="loadingFailed" 
      class="absolute inset-0 z-[1000] bg-[#0f172a] flex flex-col items-center justify-center"
    >
      <div class="p-6 bg-white rounded-lg shadow-lg max-w-md">
        <h3 class="text-xl font-bold mb-4 text-red-600">Loading Issue</h3>
        <p class="mb-4">There was a problem loading the animation frames.</p>
        <div class="flex flex-col space-y-3">
          <button 
            @click="retryLoading" 
            class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry Loading
          </button>
          <button 
            @click="skipAnimation" 
            class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Skip Animation
          </button>
        </div>
      </div>
    </div>
    
    <!-- Loading indicator -->
    <div 
      v-if="!sequenceReady && !loadingFailed" 
      class="absolute inset-0 z-[999] bg-[#0f172a] flex items-center justify-center"
    >
      <div class="flex flex-col items-center">
        <svg class="animate-spin h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="mt-3 text-white/80 text-sm">Loading frames...</span>
      </div>
    </div>
    
    <!-- Sequence container -->
    <div class="relative w-full h-full sequence-container" ref="sequenceContainer">
      <!-- Image sequence element -->
      <div class="absolute top-0 left-0 w-full h-full">
        <img 
          v-if="currentFrameSrc" 
          :src="currentFrameSrc" 
          ref="currentFrameImg"
        class="absolute top-0 left-0 w-full h-full object-cover"
          alt="Tree sequence frame"
        />
      </div>
      
      <!-- Scroll indicator for initial sequence scrubbing -->
      <div v-if="sequenceReady && !sequenceLocked" class="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center" ref="scrollIndicator">
        <span class="text-white/80 text-sm mb-2">Scroll to grow the tree</span>
        <div class="w-px h-16 bg-gradient-to-b from-white/80 to-transparent"></div>
      </div>
      
      <!-- Continue scrolling indicator when sequence is locked -->
      <div v-if="sequenceReady && sequenceLocked" class="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center animate-pulse" ref="continueScrolling">
        <span class="text-white/80 text-sm mb-2">Continue scrolling</span>
        <svg class="w-8 h-8 text-white/80" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
        </svg>
      </div>
      
      <!-- Interaction blocker overlay - shown when permanently locked -->
      <div 
        v-if="permanentlyLocked && !scrollEnabledAfterLock" 
        class="absolute inset-0 z-[1001]"
        @click.prevent
        @touchstart.prevent
        @touchmove.prevent
        @mousedown.prevent
        @mousemove.prevent
      ></div>
    </div>
  </section>
  
  <!-- Hidden marker to enable scrolling trigger only, no excess space -->
  <div id="scroll-spacer" style="height: 5px; background-color: transparent;"></div>
</template>

<script>
import { gsap } from '@gsap/shockingly'
import { ScrollTrigger } from '@gsap/shockingly/ScrollTrigger'

// Global image cache to ensure each image is loaded only once across the entire application
const globalImageCache = new Map();
const alreadyRequested = new Set();

// Preload a single image and cache the result
function preloadImage(src) {
  // If already in global cache, return the cached entry
  if (globalImageCache.has(src)) {
    return globalImageCache.get(src);
  }
  
  // If already requested but not yet cached, don't request again
  if (alreadyRequested.has(src)) {
    return src;
  }
  
  // Mark as requested to prevent duplicates
  alreadyRequested.add(src);
  
  // Add to cache immediately (the URL itself is the value)
  globalImageCache.set(src, src);
  
  return src;
}

// Load the first frame directly to have it available immediately
const frame1Path = require('@/assets/images/tree/output_0001.webp');
globalImageCache.set(frame1Path, frame1Path);
alreadyRequested.add(frame1Path);

// Frame sequence controller class - SIMPLIFIED to use global cache
class FrameSequenceController {
  constructor(options = {}) {
    this.totalFrames = options.totalFrames || 240;
    this.lockFrame = options.lockFrame || this.totalFrames;
    this.pingPongStartFrame = options.pingPongStartFrame || this.totalFrames - 10;
    
    // Simply create a lookup table for frame paths to avoid repeated webpack requires
    this.framePaths = new Array(this.totalFrames + 1);
    this.framePaths[1] = frame1Path;
  }
  
  // Get a frame path - compute once only per frame number
  getFramePath(frameNumber) {
    frameNumber = Math.max(1, Math.min(this.totalFrames, Math.round(frameNumber)));
    
    // If we already computed this path, return it
    if (this.framePaths[frameNumber]) {
      return this.framePaths[frameNumber];
    }
    
    try {
      // Compute the path only once per frame
      const paddedNumber = String(frameNumber).padStart(4, '0');
      const path = require(`@/assets/images/tree/output_${paddedNumber}.webp`);
      
      // Store in our lookup table AND use global preload to prevent duplicates
      this.framePaths[frameNumber] = preloadImage(path);
      
      return this.framePaths[frameNumber];
      } catch (error) {
      console.error(`Error getting frame path ${frameNumber}:`, error);
      return null;
    }
  }
  
  // Simplified preload - just computes the paths, actual loading is handled by browser
  async preloadInitialFrames() {
    for (let i = 1; i <= 5; i++) {
      this.getFramePath(i);
    }
    return frame1Path;
  }
  
  // Just get the path - no actual image loading logic
  async requestFrame(frameNumber) {
    return this.getFramePath(frameNumber);
  }
  
  // Just get the path - no actual image loading logic
  async loadFrame(frameNumber) {
    return this.getFramePath(frameNumber);
  }
}

export default {
  name: 'HeroSection',
  data() {
    return {
      sequenceReady: false,
      currentFrame: 1,
      totalFrames: 240,
      lockFrame: 240,
      pingPongStartFrame: 230,
      sequenceLocked: false,
      permanentlyLocked: false,
      scrollEnabledAfterLock: false,
      currentFrameSrc: null,
      frameController: null,
      scrollProgress: 0,
      touchStartY: null,
      touchStartX: null,
      retryCount: 0,
      loadingFailed: false,
      showDebugControls: false,
      keySequence: [],
      scrollHandler: null,
      touchStartHandler: null,
      touchMoveHandler: null,
      mouseDownHandler: null,
      mouseMoveHandler: null,
      mouseUpHandler: null,
      isMouseDown: false,
      lastMouseY: 0,
      scrollThrottle: 16, // ~60fps throttling
      framesPerScroll: 1,
      scrollMultiplier: 4.0,
      mobileFrameSkip: 3, // Skip frames on mobile to improve performance
      mobileScrollMultiplier: 2.2, // Increased from 1.2 for faster scrolling
      mobileStepSize: 1.2, // Increased from 0.8 for faster scrolling
      desktopStepSize: 2.0,
      useSmoothScrubbing: true,
      scrubDuration: 0.15, // Even shorter for immediate feedback
      touchThrottleDelay: 32, // Increase throttling to reduce processing load
      maxFrameJump: 3, // Stricter limit on frame jumps
      scrollEnabled: false,
      scrollLocked: false,
      isMobile: false,
      scrubAnimation: null,
      pingPongAnimation: null,
      framesLoaded: 0,
      loadingAllFrames: false,
      scrollTriggerInstance: null,
      lastScrollTime: 0,
      useMobileOptimization: true, // Enable mobile optimizations
    }
  },
  computed: {
    // Smoother frame calculation
    smoothedTime() {
      return this.currentFrame / this.totalFrames;
    }
  },
  mounted() {
    // Force to top of page
    window.scrollTo(0, 0);
    
    // Detect mobile device
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Initialize frame sequence controller
    this.frameController = new FrameSequenceController({
      totalFrames: this.totalFrames,
      lockFrame: this.lockFrame,
      pingPongStartFrame: this.pingPongStartFrame
    });
    
    // Make sure frame controller is properly initialized
    if (!this.frameController) {
      console.error("Frame controller not properly initialized");
          this.loadingFailed = true;
          return;
        }
        
    // Register GSAP plugin
    if (typeof gsap !== 'undefined' && gsap.registerPlugin) {
      try {
        gsap.registerPlugin(ScrollTrigger);
      } catch (error) {
        console.warn('Error registering ScrollTrigger:', error);
      }
    }
    
    // Initial frame loading
    this.initializeSequence();
    
    // Setup debug controls with konami code (up, up, down, down, left, right, left, right, b, a)
    document.addEventListener('keydown', this.handleKeySequence);
    
    // Force refresh of ScrollTrigger after everything is mounted
    this.$nextTick(() => {
            setTimeout(() => {
        this.refreshScrollTrigger();
      }, 500);
    });
  },
  beforeUnmount() {
    // Clean up all event listeners and resources
    this.cleanupBeforeDestroy();
  },
  methods: {
    // Setup GSAP ScrollTrigger
    setupScrollTrigger() {
      try {
        if (!gsap || !ScrollTrigger) {
          console.error("GSAP or ScrollTrigger not available");
          return;
        }
        
        // Make sure GSAP ScrollTrigger plugin is registered
        gsap.registerPlugin(ScrollTrigger);
        
        // Enable scrolling before creating ScrollTrigger
        document.body.style.overflow = 'auto';
        
        // Force a small delay to ensure DOM is ready
        setTimeout(() => {
          // Create ScrollTrigger for the hero section
          this.scrollTriggerInstance = ScrollTrigger.create({
            trigger: this.$refs.heroSection,
            start: "top top",
            end: "bottom top",
            pin: true,
            pinSpacing: true,
            scrub: 0.5,
            anticipatePin: 1,
            markers: this.showDebugControls, // Only show markers in debug mode
            onUpdate: self => {
              // If permanently locked, don't allow any further interactions
              if (this.permanentlyLocked) return;
              
              if (this.sequenceLocked) return;
              
              // Map progress (0-1) to frame index (1-totalFrames)
              const targetFrame = Math.round(self.progress * (this.totalFrames - 1) + 1);
              this.updateToFrame(targetFrame);
              
              // Lock the sequence if we've reached the lock frame
              if (targetFrame >= this.lockFrame && !this.sequenceLocked) {
                this.lockSequence();
              }
            },
            onLeave: () => {
              // Ensure animation is locked when scrolling past
              if (!this.sequenceLocked) {
                this.lockSequence();
              }
              
              // Set permanent lock
              this.permanentlyLocked = true;
            },
            onEnterBack: () => {
              // Make sure we're still locked when scrolling back
              if (this.permanentlyLocked) {
                this.disableAllInteractions();
              }
            },
            onLeaveBack: () => {
              // Ensure permanent lock is maintained
              if (this.sequenceLocked) {
                this.permanentlyLocked = true;
              }
            }
          });
          
          // Setup mouse and touch handlers for additional input methods
          this.setupMouseDragHandlers();
          this.setupTouchHandlers();
          
          // Setup scroll indicator after DOM ready
          this.$nextTick(() => {
            this.setupScrollIndicator();
          });
        }, 200);
        
        return this.scrollTriggerInstance;
      } catch (error) {
        console.error("Error setting up ScrollTrigger:", error);
        return null;
      }
    },
    
    // Initialize the image sequence
    async initializeSequence() {
      try {
        // Set overflow to auto to enable scrolling
        document.body.style.overflow = 'auto';
        
        // Start by loading the first frame - ignore the returned value since we use frame1Path
        await this.frameController.preloadInitialFrames();
        
        // Set the current frame source
        this.currentFrameSrc = frame1Path;
        
        // Apply mobile optimizations if needed
        this.detectAndApplyMobileOptimizations();
        
        // Mark sequence as ready once first frame is loaded
        this.sequenceReady = true;
        
        // Setup ScrollTrigger after sequence is ready
        this.$nextTick(() => {
          this.setupScrollTrigger();
        });
        
        // Continue preloading frames in the background
        this.preloadRemainingFrames();
      } catch (error) {
        console.error("Error initializing sequence:", error);
        this.loadingFailed = true;
      }
    },
    
    // Detect and apply mobile optimizations
    detectAndApplyMobileOptimizations() {
      // Check if running on mobile
      this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (this.isMobile && this.useMobileOptimization) {
        console.log("Applying mobile optimizations");
        
        // Reduce total frames for mobile by using frame skipping
        const effectiveFrames = Math.ceil(this.totalFrames / this.mobileFrameSkip);
        console.log(`Mobile optimization: using ${effectiveFrames} effective frames with skip ${this.mobileFrameSkip}`);
        
        // Force image decoding to be asynchronous to prevent UI blocking
        if (this.$refs.currentFrameImg) {
          this.$refs.currentFrameImg.decoding = "async";
        }
      }
    },
    
    // Preload all frames in one go
    preloadRemainingFrames() {
      if (this.loadingAllFrames) return;
      
      this.loadingAllFrames = true;
      
      // On mobile, only preload a subset of frames to reduce memory usage
      const frameSkip = (this.isMobile && this.useMobileOptimization) ? this.mobileFrameSkip : 1;
      
      // Precompute frame paths in batches with appropriate skipping
      const totalBatches = Math.ceil((this.totalFrames - 1) / 20);
      let batchesCompleted = 0;
      
      const preloadBatch = (start, end) => {
        // Just compute paths for this batch with frame skipping on mobile
        for (let i = start; i <= end; i += frameSkip) {
          this.frameController.getFramePath(i);
        }
        
        batchesCompleted++;
        if (batchesCompleted >= totalBatches) {
          this.loadingAllFrames = false;
        }
      };
      
      // Prioritize frames that will be used most often
      const priorityFrames = [1, 
        Math.round(this.totalFrames * 0.25), 
        Math.round(this.totalFrames * 0.5), 
        Math.round(this.totalFrames * 0.75), 
        this.lockFrame
      ];
      
      // Preload priority frames first
      for (const frame of priorityFrames) {
        this.frameController.getFramePath(frame);
      }
      
      // Split into batches of 20 frames
      for (let batch = 0; batch < totalBatches; batch++) {
        const start = batch * 20 + 2; // Start from frame 2 (frame 1 already loaded)
        const end = Math.min(start + 19, this.totalFrames);
        
        // Schedule each batch with increasing delay
        setTimeout(() => {
          preloadBatch(start, end);
        }, batch * 150); // Increased delay between batches
      }
    },
    
    // Update to a specific frame
    async updateToFrame(frameNumber, animate = true) {
      // If permanently locked, don't allow updates
      if (this.permanentlyLocked && frameNumber < this.lockFrame) {
        frameNumber = this.lockFrame;
      }
      
      if (this.sequenceLocked && frameNumber >= this.lockFrame) {
        frameNumber = this.lockFrame;
      }
      
      frameNumber = Math.max(1, Math.min(this.totalFrames, Math.round(frameNumber)));
      
      // Calculate frame difference
      const frameDiff = Math.abs(frameNumber - this.currentFrame);
      
      // Skip update if the frame is already displayed
      if (frameNumber === this.currentFrame && !this.sequenceLocked) {
        return;
      }
      
      // Kill any existing animation
      if (this.scrubAnimation) {
        this.scrubAnimation.kill();
      }
      
      // For production environment, handle large jumps differently
      if (frameDiff > this.maxFrameJump) {
        // For large jumps, use intermediate frames to prevent skipping
        const direction = frameNumber > this.currentFrame ? 1 : -1;
        const intermediateFrames = [];
        const totalIntermediateFrames = Math.min(3, Math.floor(frameDiff / 2));
        
        for (let i = 1; i <= totalIntermediateFrames; i++) {
          const progress = i / (totalIntermediateFrames + 1);
          const intermediateFrame = Math.round(this.currentFrame + direction * progress * frameDiff);
          intermediateFrames.push(intermediateFrame);
        }
        
        // Preload intermediate frames to ensure they're ready
        intermediateFrames.forEach(frame => {
          this.frameController.getFramePath(frame);
        });
        
        // Use shorter animation duration for large jumps to maintain responsiveness
        const quickDuration = this.scrubDuration * 0.7;
        
        if (animate && this.useSmoothScrubbing) {
          this.scrubAnimation = gsap.to(this, {
            currentFrame: frameNumber,
            duration: quickDuration,
            ease: "power3.out", // Strong acceleration for large jumps
            onUpdate: () => {
              this.updateFrameSource(Math.round(this.currentFrame));
            }
          });
        } else {
          this.currentFrame = frameNumber;
          await this.updateFrameSource(frameNumber);
        }
      } else {
        // Normal smooth animation for small frame differences
        const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const animationDuration = isMobileDevice ? this.scrubDuration * 1.1 : this.scrubDuration;
        
        if (animate && this.useSmoothScrubbing && frameDiff > 1) {
          this.scrubAnimation = gsap.to(this, {
            currentFrame: frameNumber,
            duration: animationDuration,
            ease: "power2.out",
            onUpdate: () => {
              this.updateFrameSource(Math.round(this.currentFrame));
            }
          });
        } else {
          this.currentFrame = frameNumber;
          await this.updateFrameSource(frameNumber);
        }
      }
    },
    
    // Update the frame source - SIMPLIFIED to avoid duplicate loads
    async updateFrameSource(frameNumber) {
      try {
        // Special case for frame 1 which we imported directly
        if (frameNumber === 1) {
          this.currentFrameSrc = frame1Path;
          return;
        }
        
        // Apply frame skipping on mobile (ensure we're on a valid frame)
        if (this.isMobile && this.useMobileOptimization && frameNumber > 1) {
          const remainder = frameNumber % this.mobileFrameSkip;
          if (remainder !== 0) {
            frameNumber = frameNumber - remainder + (remainder > this.mobileFrameSkip / 2 ? this.mobileFrameSkip : 0);
          }
        }
        
        // Just get the frame path - no actual loading logic
        const frameSrc = this.frameController.getFramePath(frameNumber);
        if (frameSrc) {
          // Update the frame source (no async loading needed since it's already cached)
          this.currentFrameSrc = frameSrc;
        }
      } catch (error) {
        console.error(`Error updating frame source to frame ${frameNumber}:`, error);
      }
    },
    
    // Lock the sequence and start ping-pong animation
    lockSequence() {
      if (this.sequenceLocked) return;
      
      this.sequenceLocked = true;
      this.permanentlyLocked = true; // Set permanent lock
      
      // Ensure we're on the lock frame
      this.updateToFrame(this.lockFrame, true);
      
      // Start ping-pong animation
      this.$nextTick(() => {
        this.startPingPongAnimation();
      });
      
      // Tell App we're ready to show content
      window.dispatchEvent(new CustomEvent('heroVideoLocked', { 
        detail: { ready: true } 
      }));
      
      // Release scroll but keep section pinned until user scrolls away
      this.$nextTick(() => {
        // Kill the ScrollTrigger to completely remove its pin effect
        if (this.scrollTriggerInstance) {
          this.scrollTriggerInstance.kill();
          this.scrollTriggerInstance = null;
          
          // Force ScrollTrigger to refresh
          if (ScrollTrigger && ScrollTrigger.refresh) {
            ScrollTrigger.refresh(true);
          }
        }
        
        // Set scroll enabled flag
        this.scrollEnabledAfterLock = true;
        
        // Allow scrolling explicitly
        document.body.style.overflow = 'auto';
        document.documentElement.style.overflow = 'auto';
        
        // Disable all interaction handlers
        this.disableAllInteractions();
      });
    },
    
    // Disable all interaction handlers once locked
    disableAllInteractions() {
      // Remove mouse handlers
      if (this.$refs.sequenceContainer) {
        this.$refs.sequenceContainer.removeEventListener('mousedown', this.mouseDownHandler);
        this.$refs.sequenceContainer.removeEventListener('mousemove', this.mouseMoveHandler);
        this.$refs.sequenceContainer.removeEventListener('click', this.mouseDownHandler, true);
      }
      
      // Remove touch handlers
      document.removeEventListener('touchstart', this.touchStartHandler, { passive: false });
      document.removeEventListener('touchmove', this.touchMoveHandler, { passive: false });
      document.removeEventListener('touchend', this.handleTouchStart, true);
      
      // Remove mouse handlers from document as well
      document.removeEventListener('mousedown', this.mouseDownHandler, true);
      document.removeEventListener('mousemove', this.mouseMoveHandler, true);
      document.removeEventListener('mouseup', this.mouseUpHandler, true);
      
      // Set flags to prevent further interactions
      this.isMouseDown = false;
      this.touchStartY = null;
      this.touchStartX = null;
      
      // Make sure the sequence stays locked
      this.sequenceLocked = true;
      this.permanentlyLocked = true;
      this.scrollEnabledAfterLock = true;
      
      // Allow scrolling again
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
    },
    
    // Start ping-pong animation between pingPongStartFrame and lockFrame
    startPingPongAnimation() {
      if (!gsap) {
        console.error("GSAP not available for ping-pong animation");
        return;
      }

      // Kill any existing animation
      if (this.pingPongAnimation) {
        this.pingPongAnimation.kill();
      }
      
      // Pre-cache all paths for ping-pong animation
      this.precachePingPongFrames();
      
      // Create a ping-pong timeline that loops between frames
      try {
        const timeline = gsap.timeline({
          repeat: -1, // Infinite repeat
          yoyo: true, // Automatic ping-pong effect
        });
        
        timeline.to(this, {
          currentFrame: this.lockFrame,
          duration: 1.5,
          ease: "power1.inOut",
          onUpdate: () => {
            this.updateFrameSource(Math.round(this.currentFrame));
          }
        }).to(this, {
          currentFrame: this.pingPongStartFrame,
          duration: 1.5,
          ease: "power1.inOut",
          onUpdate: () => {
            this.updateFrameSource(Math.round(this.currentFrame));
          }
        });
        
        this.pingPongAnimation = timeline;
      } catch (error) {
        console.error("Error creating ping-pong animation:", error);
      }
    },
    
    // Precache frames needed for ping-pong animation
    precachePingPongFrames() {
      // Pre-compute ALL frame paths for ping-pong
      for (let i = this.pingPongStartFrame; i <= this.lockFrame; i++) {
        this.frameController.getFramePath(i);
      }
    },
    
    // Setup mouse drag handlers for alternative interaction
    setupMouseDragHandlers() {
      // Create bound handler functions
      this.mouseDownHandler = this.handleMouseDown.bind(this);
      this.mouseMoveHandler = this.handleMouseMove.bind(this);
      this.mouseUpHandler = this.handleMouseUp.bind(this);
      
      // Add event listeners
      this.$refs.sequenceContainer.addEventListener('mousedown', this.mouseDownHandler);
      document.addEventListener('mousemove', this.mouseMoveHandler);
      document.addEventListener('mouseup', this.mouseUpHandler);
      document.addEventListener('mouseleave', this.mouseUpHandler);
    },
    
    // Handle mouse down event
    handleMouseDown(event) {
      if (!this.sequenceReady || this.sequenceLocked) return;
      
      this.isMouseDown = true;
      this.lastMouseY = event.clientY;
      
      // Prevent default behavior
      event.preventDefault();
    },
    
    // Handle mouse move event
    handleMouseMove(event) {
      // If permanently locked, block all interactions
      if (this.permanentlyLocked) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      
      if (!this.sequenceReady || !this.isMouseDown || this.sequenceLocked) return;
      
      // Calculate vertical movement
      const deltaY = this.lastMouseY - event.clientY;
      
      // Calculate step size based on movement
      const stepSize = this.desktopStepSize * 0.7; // Reduced step size for mouse drag
      
      // Calculate frame step
      const frameStep = Math.sign(deltaY) * stepSize * this.scrollMultiplier;
      
      // Calculate target frame
      let targetFrame = this.currentFrame + frameStep;
      
      // Clamp target frame
      targetFrame = Math.max(1, Math.min(this.lockFrame, Math.round(targetFrame)));
      
      // Update to the new frame
      this.updateToFrame(targetFrame, this.useSmoothScrubbing);
      
      // Lock sequence if we've reached the end
      if (targetFrame >= this.lockFrame && !this.sequenceLocked) {
        this.lockSequence();
      }
      
      // Update last mouse position
      this.lastMouseY = event.clientY;
    },
    
    // Handle mouse up event
    handleMouseUp() {
      this.isMouseDown = false;
    },
    
    // Setup touch handlers for mobile
    setupTouchHandlers() {
      // Create bound handler functions
      this.touchStartHandler = this.handleTouchStart.bind(this);
      this.touchMoveHandler = this.handleTouchMove.bind(this);
      
      // Add event listeners - make sure to mark as passive: false to allow preventDefault
      document.addEventListener('touchstart', this.touchStartHandler, { passive: false });
      document.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
    },
    
    // Handle touch start event
    handleTouchStart(event) {
      if (!this.sequenceReady || this.sequenceLocked) return;
      
      this.touchStartY = event.touches[0].clientY;
      this.touchStartX = event.touches[0].clientX;
      
      // Don't prevent default here to allow normal scrolling after animation is locked
      if (!this.sequenceLocked) {
        event.preventDefault();
      }
    },
    
    // Handle touch move event
    handleTouchMove(event) {
      // If permanently locked, DON'T block interactions - allow normal scrolling
      if (this.permanentlyLocked) {
        return; // Don't prevent default to allow page scrolling
      }
      
      if (!this.sequenceReady || !this.touchStartY || this.sequenceLocked) return;
      
      // More aggressive throttling on mobile to prevent event flooding
      const now = Date.now();
      if (now - this.lastScrollTime < this.touchThrottleDelay) {
        return;
      }
      this.lastScrollTime = now;

      // Calculate vertical and horizontal movement
      const touchY = event.touches[0].clientY;
      const touchX = event.touches[0].clientX;
      const deltaY = this.touchStartY - touchY;
      const deltaX = this.touchStartX - touchX;
      
      // If horizontal movement is greater than vertical, ignore
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        return;
      }
      
      // Only prevent default if we're still controlling the animation and not locked
      if (!this.sequenceLocked) {
        event.preventDefault();
      }
      
      // Apply very minimal processing for touch events on mobile
      const stepSize = this.mobileStepSize;
      
      // Apply a smaller sensitivity factor to reduce speed
      const sensitivityFactor = 0.9; // Increased from 0.7 for faster scrolling
      
      // Mobile-optimized simpler calculation to reduce processing
      let frameStep = Math.sign(deltaY) * stepSize * this.mobileScrollMultiplier * sensitivityFactor;
      
      // For small movements, reduce sensitivity further
      if (Math.abs(deltaY) < 10) {
        frameStep *= 0.7; // Increased from 0.5 for faster scrolling on small movements
      }
      
      // Hard limit frame jumps to prevent large jumps
      frameStep = Math.sign(frameStep) * Math.min(Math.abs(frameStep), this.maxFrameJump);
      
      // Apply frame skipping on mobile for performance 
      if (this.isMobile && this.useMobileOptimization) {
        frameStep *= this.mobileFrameSkip;
      }
      
      // Calculate target frame with simple math
      let targetFrame = Math.round(this.currentFrame + frameStep);
      
      // Clamp target frame
      targetFrame = Math.max(1, Math.min(this.lockFrame, targetFrame));
      
      // Update to the new frame with optimized animation
      this.updateToFrameOptimized(targetFrame);
      
      // Lock sequence if we've reached the end
      if (targetFrame >= this.lockFrame && !this.sequenceLocked) {
        this.lockSequence();
      }
      
      // Update touch start position for continuous movement
      this.touchStartY = touchY;
      this.touchStartX = touchX;
    },
    
    // Optimized frame update for better performance
    updateToFrameOptimized(frameNumber) {
      // If permanently locked, don't allow updates
      if (this.permanentlyLocked && frameNumber < this.lockFrame) {
        frameNumber = this.lockFrame;
      }
      
      if (this.sequenceLocked && frameNumber >= this.lockFrame) {
        frameNumber = this.lockFrame;
      }
      
      frameNumber = Math.max(1, Math.min(this.totalFrames, Math.round(frameNumber)));
      
      // Skip update if the frame is already displayed
      if (frameNumber === this.currentFrame) {
        return;
      }
      
      // Kill any existing animation
      if (this.scrubAnimation) {
        this.scrubAnimation.kill();
      }
      
      // On mobile, use simpler, more direct updates for performance
      if (this.isMobile && this.useMobileOptimization) {
        // On mobile, skip frames for performance
        const mobileAdjustedFrame = Math.ceil(frameNumber / this.mobileFrameSkip) * this.mobileFrameSkip;
        
        // Set the current frame immediately without animation
        this.currentFrame = mobileAdjustedFrame;
        this.updateFrameSource(mobileAdjustedFrame);
      } else {
        // Desktop can handle smoother animations
        if (this.useSmoothScrubbing) {
          this.scrubAnimation = gsap.to(this, {
            currentFrame: frameNumber,
            duration: this.scrubDuration,
            ease: "none", // Linear animation for consistent speed
            onUpdate: () => {
              this.updateFrameSource(Math.round(this.currentFrame));
            }
          });
        } else {
          this.currentFrame = frameNumber;
          this.updateFrameSource(frameNumber);
        }
      }
    },
    
    // Setup scroll indicator animation
    setupScrollIndicator() {
      if (!this.$refs.scrollIndicator) return;
      
      // Create subtle animation for scroll indicator
      gsap.to(this.$refs.scrollIndicator, {
        y: 10,
        duration: 1.5,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });
    },
    
    // Handle key sequence for debug controls (konami code)
    handleKeySequence(event) {
      const konamiCode = [38, 38, 40, 40, 37, 39, 37, 39, 66, 65]; // up, up, down, down, left, right, left, right, b, a
      
      this.keySequence.push(event.keyCode);
      
      if (this.keySequence.length > konamiCode.length) {
        this.keySequence.shift();
      }
      
      if (this.keySequence.join(',') === konamiCode.join(',')) {
        this.showDebugControls = !this.showDebugControls;
        this.keySequence = [];
      }
    },
    
    // Manually set frame from debug control
    manualSetFrame(value) {
      const frame = parseInt(value, 10);
      this.updateToFrame(frame, false);
      
      // Lock sequence if we've reached the end
      if (frame >= this.lockFrame && !this.sequenceLocked) {
        this.lockSequence();
      }
    },
    
    // Manually set lock state from debug control
    manualSetLocked(value) {
      if (value === true && !this.sequenceLocked) {
        this.lockSequence();
      } else if (value === false && this.sequenceLocked) {
        // Unlock sequence (for debug only)
        this.sequenceLocked = false;
        
        // Kill ping-pong animation
        if (this.pingPongAnimation) {
          this.pingPongAnimation.kill();
          this.pingPongAnimation = null;
        }
      }
    },
    
    // Release scroll lock
    releaseScroll() {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
      this.scrollLocked = true;
      
      // Tell the app that hero is complete
      window.dispatchEvent(new CustomEvent('heroComplete', { 
        detail: { heroStillAccessible: true } 
      }));
    },
    
    // Retry loading if failed
    retryLoading() {
      this.loadingFailed = false;
      this.retryCount = 0;
      this.initializeSequence();
    },
    
    // Skip animation completely
    skipAnimation() {
      this.sequenceLocked = true;
      this.currentFrame = this.lockFrame;
        this.releaseScroll();
      
      // Tell the app that hero is complete
      window.dispatchEvent(new CustomEvent('heroComplete', { 
        detail: { heroStillAccessible: true } 
      }));
    },
    
    // Clean up resources before component is destroyed
    cleanupBeforeDestroy() {
      // Remove event listeners
      document.removeEventListener('keydown', this.handleKeySequence);
      
      if (this.$refs.sequenceContainer) {
        this.$refs.sequenceContainer.removeEventListener('mousedown', this.mouseDownHandler);
      }
      
      document.removeEventListener('mousemove', this.mouseMoveHandler);
      document.removeEventListener('mouseup', this.mouseUpHandler);
      document.removeEventListener('mouseleave', this.mouseUpHandler);
      document.removeEventListener('touchstart', this.touchStartHandler, { passive: false });
      document.removeEventListener('touchmove', this.touchMoveHandler, { passive: false });
      
      // Kill any existing animations
      if (this.scrubAnimation) {
        this.scrubAnimation.kill();
      }
      
      if (this.pingPongAnimation) {
        this.pingPongAnimation.kill();
      }
      
      // Kill ScrollTrigger instance
      if (this.scrollTriggerInstance) {
        this.scrollTriggerInstance.kill();
        this.scrollTriggerInstance = null;
      }
      
      // Kill all ScrollTrigger instances
      try {
        if (typeof ScrollTrigger !== 'undefined' && ScrollTrigger.getAll) {
          ScrollTrigger.getAll().forEach(trigger => trigger.kill());
          ScrollTrigger.clearScrollMemory();
          ScrollTrigger.refresh(true);
        }
      } catch (err) {
        console.warn('Error cleaning up ScrollTrigger:', err);
      }
      
      // Allow scrolling again
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
    },
    
    // Method to refresh ScrollTrigger
    refreshScrollTrigger() {
      try {
        if (ScrollTrigger && ScrollTrigger.refresh) {
          ScrollTrigger.refresh();
        }
      } catch (error) {
        console.error("Error refreshing ScrollTrigger:", error);
      }
    }
  }
}
</script>

<style scoped>
.sequence-container {
  background-color: #0f172a; /* Dark background while frames load */
}

img {
  will-change: transform; /* Optimize for animations */
  backface-visibility: hidden; /* Prevent flickering */
  -webkit-backface-visibility: hidden;
  -webkit-perspective: 1000;
  perspective: 1000;
}

/* Hero section base styles */
#hero {
  height: 100vh;
  width: 100%;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

/* Ensure proper z-index */
.z-50 {
  z-index: 50;
}

/* Default image styling for all screens - fullscreen coverage */
img {
  object-fit: cover;
}

/* Mobile screens - maintain fullscreen but position to show full tree */
@media (max-width: 768px) {
  img {
    object-fit: cover; /* Keep fullscreen */
    object-position: 50% 40%; /* Focus on the tree by positioning up slightly */
  }
}

/* Smaller mobile screens need more focused positioning */
@media (max-width: 480px) {
  img {
    object-position: 50% 35%; /* Move focus point higher on smaller devices */
  }
}

/* Extra small screens like iPhone SE */
@media (max-width: 375px) {
  img {
    object-position: 50% 30%; /* Further adjustment for very small devices */
  }
}

/* Pulse animation */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s infinite;
}

/* Loading spinner animation */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
</style>